import { dateKey } from '../nutrition/nutrition'

/**
 * Body measurements. Deliberately tiny: a current value, a goal, and whether the trend is
 * going the right way over the last month.
 *
 * PURE — "now" is always passed in.
 */

export type MeasurementKind = 'weight' | 'booty'

export interface Measurement {
  id: string
  kind: MeasurementKind
  /** kg for weight, cm for booty. */
  value: number
  date: string
  createdAt: string
}

export interface HealthGoals {
  weightKg: number | null
  bootyCm: number | null
}

export interface HealthState {
  goals: HealthGoals
  measurements: Measurement[]
}

export interface TrackerView {
  kind: MeasurementKind
  label: string
  unit: string
  current: number | null
  currentDate: string | null
  goal: number | null
  /** Signed change against the oldest reading inside the window. */
  changePct: number | null
  changeAbs: number | null
  windowDays: number
  toGoal: number | null
  history: Measurement[]
}

export const MEASUREMENT_LABELS: Record<MeasurementKind, { label: string; unit: string }> = {
  weight: { label: 'Weight', unit: 'kg' },
  booty: { label: 'Booty', unit: 'cm' },
}

export const TREND_WINDOW_DAYS = 30

export function emptyHealth(): HealthState {
  return { goals: { weightKg: null, bootyCm: null }, measurements: [] }
}

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000)
}

/**
 * Percentage change is measured against the oldest reading still inside the window, not
 * against the goal — that way it answers "am I moving?" rather than "am I there yet",
 * which is the question a month of data can actually answer.
 */
export function trackerView(
  state: HealthState,
  kind: MeasurementKind,
  now: Date = new Date()
): TrackerView {
  const today = dateKey(now)
  const all = state.measurements
    .filter((m) => m.kind === kind)
    .sort((a, b) => a.date.localeCompare(b.date) || a.createdAt.localeCompare(b.createdAt))

  const latest = all.at(-1) ?? null
  const inWindow = all.filter((m) => daysBetween(m.date, today) <= TREND_WINDOW_DAYS)
  const baseline = inWindow.length > 1 ? inWindow[0] : null

  const goal = kind === 'weight' ? state.goals.weightKg : state.goals.bootyCm

  const changeAbs = latest && baseline ? latest.value - baseline.value : null
  const changePct =
    latest && baseline && baseline.value !== 0
      ? ((latest.value - baseline.value) / baseline.value) * 100
      : null

  return {
    kind,
    label: MEASUREMENT_LABELS[kind].label,
    unit: MEASUREMENT_LABELS[kind].unit,
    current: latest?.value ?? null,
    currentDate: latest?.date ?? null,
    goal,
    changePct,
    changeAbs,
    windowDays: TREND_WINDOW_DAYS,
    toGoal: latest && goal !== null ? goal - latest.value : null,
    history: [...inWindow].reverse(),
  }
}

export function parseMeasurement(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.')
  if (trimmed === '') return null
  if (!/^\d*\.?\d*$/.test(trimmed)) return null
  const value = Number(trimmed)
  if (!Number.isFinite(value) || value <= 0) return null
  return Math.round(value * 10) / 10
}

export function formatMeasurement(value: number | null, unit: string): string {
  if (value === null) return '—'
  return `${Math.round(value * 10) / 10} ${unit}`
}

export function formatPct(pct: number | null): string {
  if (pct === null) return '—'
  const rounded = Math.round(pct * 10) / 10
  return `${rounded > 0 ? '+' : ''}${rounded}%`
}
